import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { MatIcon } from '@angular/material/icon';

import { ChatThread, ThreadType } from '@lib/chat/models/chat.interface';
import { selectChatDraftsMap } from '@lib/chat/store/chat.selectors';

import { MemberProfile } from '@lib/core/models/member.interface';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';

@Component({
  selector: 'chat-thread',
  standalone: true,
  imports: [DatePipe, NgClass, MatIcon, SpinLoadingComponent],
  templateUrl: './chat-thread.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatThreadComponent {
  private readonly store = inject(Store);

  readonly selected = input.required<boolean>();
  readonly isOdd = input.required<boolean>();
  readonly unreadCount = input.required<number>();
  readonly thread = input.required<ChatThread>();
  readonly ownUserId = input.required<string>();

  readonly selectUserChannel = output<MemberProfile>();
  readonly pinThread = output<string>();
  readonly unpinThread = output<string>();

  protected readonly pinLoading = signal<boolean>(false);

  protected readonly hasDraft = computed(() => {
    const draftsMap = this.store.selectSignal(selectChatDraftsMap)();
    const threadId = this.thread().id;
    return draftsMap.has(threadId);
  });

  getThreadTypeLabel(threadType: ThreadType) {
    switch (threadType) {
      case ThreadType.HealthManagerChannel:
        return 'HM';
      case ThreadType.SupportChannel:
        return 'Support';
    }
  }

  getThreadTypeLabelColor(threadType: ThreadType, isSelected: boolean) {
    switch (threadType) {
      case ThreadType.SupportChannel:
        return isSelected ? 'bg-gray-1' : 'bg-yellow-700';
      case ThreadType.HealthManagerChannel:
        return isSelected ? 'bg-gray-1' : 'bg-success-800';
    }
  }

  protected isThreadPinned(): boolean {
    return this.ownUserId() ? this.thread().assignees?.includes(this.ownUserId()) : false;
  }

  protected onTogglePin(event: Event) {
    event.stopPropagation();
    this.pinLoading.set(true);

    if (this.isThreadPinned()) this.unpinThread.emit(this.thread().id);
    else this.pinThread.emit(this.thread().id);

    this.pinLoading.set(false);
  }
}
